import { TLearningProgressLearnerOutputDto } from "@/api/services/main/learning-progress/dtos/get-learning-progress-learners.dto";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { convertDateFromIsoString } from "@/features/company/utiles/datetime";
import { useTable } from "@/hooks/useTable";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { divider } from "@/theme/colors";
import { USER_TYPE } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

type TLearningUserTableProps = {
  rows: TLearningProgressLearnerOutputDto[];
  isLoading?: boolean;
};

import { formatSecondsToHours } from "@/utils/number";

export const LearningUserTable: FC<TLearningUserTableProps> = ({ rows, isLoading }) => {
  const navigate = useNavigate();
  const { industryId } = useParams<{ industryId: string }>();
  const { userType } = useInitSetup();
  const isRsoUser = userType === USER_TYPE.RSO_USER;
  const isSupportUser = userType === USER_TYPE.SUPPORT_USER;

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, 25);
  };

  const columns = useMemo<MRT_ColumnDef<TLearningProgressLearnerOutputDto>[]>(
    () => [
      {
        id: "fullName",
        header: "氏名",
        accessorKey: "learner.fullName",
        maxSize: 151,
        Cell: ({ row }) => truncateText(row.original.fullName),
        Header: <TableSorting name="learner.fullName">氏名</TableSorting>,
      },
      ...(isRsoUser || isSupportUser
        ? [
            {
              id: "organization",
              header: "所属機関",
              accessorKey: "rsoCompany.companyName",
              maxSize: 172,
              Cell: ({ row }: { row: { original: TLearningProgressLearnerOutputDto } }) => truncateText(row.original.rsoCompany?.companyName),
              Header: <TableSorting name="rsoCompany.companyName">所属機関</TableSorting>,
            },
          ]
        : []),
      {
        id: "nationality",
        header: "国籍",
        accessorKey: "nationality.nationalityName",
        maxSize: 116,
        Cell: ({ row }) => truncateText(row.original.nationality?.nationalityName),
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
      },
      {
        id: "examSchedule",
        header: "試験予定",
        accessorKey: "activeLearnerPlan.examScheduledYearMonth",
        maxSize: 129,
        Cell: ({ row }) => truncateText(row.original.activeLearnerPlan?.examScheduledYearMonth),
        Header: <TableSorting name="activeLearnerPlan.examScheduledYearMonth">試験予定</TableSorting>,
      },
      {
        id: "lastLogin",
        header: "最終ログイン",
        accessorKey: "lastLoginTime",
        maxSize: 148,
        Cell: ({ row }) => {
          if (!row.original.user) return "(アカウント未発行)";
          if (!row.original.lastLoginTime) return "(ログインなし)";
          return convertDateFromIsoString(row.original.lastLoginTime);
        },
        Header: <TableSorting name="lastLoginTime">最終ログイン</TableSorting>,
      },
      {
        id: "totalLearningTime",
        header: "学習時間(合計)",
        accessorKey: "totalLearningSeconds",
        maxSize: 100,
        Cell: ({ row }) => formatSecondsToHours(row.original.totalLearningSeconds),
        Header: <TableSorting name="totalLearningSeconds">学習時間(合計)</TableSorting>,
      },
      {
        id: "thisWeekLearningTime",
        header: "学習時間(今週)",
        accessorKey: "thisWeekLearningSeconds",
        maxSize: 100,
        Cell: ({ row }) => formatSecondsToHours(row.original.thisWeekLearningSeconds),
        Header: <TableSorting name="thisWeekLearningSeconds">学習時間(今週)</TableSorting>,
      },
      {
        id: "curriculumProgress",
        header: "学習進捗",
        accessorKey: "curriculumProgressRate",
        maxSize: 100,
        Cell: ({ row }) => `${row.original.curriculumProgressRate}%`,
        Header: <TableSorting name="curriculumProgressRate">学習進捗</TableSorting>,
      },
      {
        id: "learnedVocabulary",
        header: "覚えた単語数",
        accessorKey: "learnedVocabularyCount",
        maxSize: 100,
        Cell: ({ row }) => `${row.original.learnedVocabularyCount}件`,
        Header: <TableSorting name="learnedVocabularyCount">覚えた単語数</TableSorting>,
      },
      {
        id: "mockExamCompletedCount",
        header: "模試実施回数",
        accessorKey: "mockExamCompletedCount",
        maxSize: 116,
        Cell: ({ row }) => `${row.original.mockExamCompletedCount}回`,
      },
      {
        id: "latestMockExamScore",
        header: "直近模試結果",
        accessorKey: "latestMockExamScore",
        maxSize: 116,
        Cell: ({ row }) =>
          row.original.latestMockExamScore != null && row.original.latestMockExamMaxScore != null
            ? `${row.original.latestMockExamScore}/${row.original.latestMockExamMaxScore}点`
            : EMPTY_STR.TEXT,
      },
      {
        id: "lastExamScore",
        header: "前回本試験点数",
        accessorKey: "lastExamScore",
        maxSize: 130,
        // 本試験の満点は業種・年度によって変動するため分母は表示しない (模試の満点を流用すると誤解を招く)。
        Cell: ({ row }) => (row.original.lastExamScore != null ? `${row.original.lastExamScore}点` : EMPTY_STR.TEXT),
      },
      {
        id: "japaneseLanguageLevel",
        header: "日本語能力",
        accessorKey: "japaneseLanguageLevel",
        maxSize: 100,
        Cell: ({ row }) => row.original.japaneseLanguageLevel?.toUpperCase() ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="japaneseLanguageLevel">日本語能力</TableSorting>,
      },
    ],
    [isRsoUser, isSupportUser],
  );

  const { table } = useTable({
    columns,
    rows,
    getRowId: (row) => row.id.toString(),
    onClickRow: (row) => {
      if (!row.user) return;
      navigate(
        getDynamicRoute(AppRoutes.learningProgressLearnerDetail, {
          industryId,
          learnerId: row.id,
        }),
      );
    },
    disableRow: (row) => !row.user,
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    renderEmptyRowsFallback: () => <Box />,
  });

  return <Table table={table} isLoading={isLoading} height="calc(100vh - 427px)" minHeight={150} borderColor={divider.high} />;
};
